import styles from "./page.module.css";
import {useAnalytics} from "@/contexts/AnalyticsContext";
import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {ProductProfitDetails, ProfitabilityByProductResponseBody} from "@boxem/core-api-schema";
import Card, {TotalCard} from "@/components/Analytics/Card";
import {Table, TableColumn} from "@/components/Table/Table";
import TextInput from "@/components/forms/TextInput/TextInput";
import Skeleton from "@/components/Skeleton/Skeleton";
import DateRangeButtons from "@/components/DateRangeSelector/DateRangeButtons";
import {useLocation} from "wouter";
import { formatValue } from "@/utils/formatUtils";
import Pagination from "@/components/Pagination/Pagination";
import { useErrorModalContext } from "@/contexts/ErrorModalContext";
import { Tooltip } from "@/components/Tooltip/Tooltip";
import IconSearch from "@/components/icons/IconSearch";
import PageContainer from "@/app/settings/container/PageContainer";
import { useCache } from "@/hooks/cacheHooks/useCache";
import { DateRangeValue, startOfToday, endOfToday } from "@/utils/dateUtils";
import { getLocalStorageItem, setLocalStorageItem } from "@/utils/localStorageUtils";
import { getSkuProfitability, getSkuProfitabilityBreakdown } from "@/api/seller-analytics";



export const computeCacheKey = (timeFrame, apiPath) => {
    return `${apiPath}?from=${new Date(timeFrame.from).getTime()}&to=${new Date(timeFrame.to).getTime()}`;
}

export interface PaginatedData<T> {
    items: T[];
    total: number;
    page: number;
    limit: number;
    totalPages: number;
}

const ProfitBySkuPage = () => {
    const { handleErrorResponseGenerically } = useErrorModalContext();
    const [timeFrame, setTimeFrame] = useState<DateRangeValue>(
        getLocalStorageItem('analyticsTimeFrame', {
          from: startOfToday.startOf("month").startOf('day').toDate(),
          to: endOfToday.toDate(),
          label: 'This month'
        })
    );

    const [page, setPage] = useState<number>(1);
    const [limit, setLimitLocal] = useState<number>(10);
    const [search, setSearchLocal] = useState<string>('');
    
    // Get saved sort preferences from localStorage
    const savedSortPrefs = getLocalStorageItem('profitBySkuSortPreferences', {
        sortedColumnId: 'revenue',
        sortDirection: 'desc'
    });
    
    const [sortedColumnId, setSortedColumnId] = useState<string | undefined>(savedSortPrefs.sortedColumnId);
    const [sortDirection, setSortDirection] = useState<'asc' | 'desc' | undefined>(savedSortPrefs.sortDirection as 'asc' | 'desc');
    
    // Track if we should stop retrying after errors
    const [shouldFetchOverview, setShouldFetchOverview] = useState(true);
    const [shouldFetchProfitability, setShouldFetchProfitability] = useState(true);

    const setSearch = useCallback((newSearch: string) => {
        setPage(1); // Reset to first page
        setSearchLocal(newSearch);
      }, []);
    
    const setLimit = useCallback((newLimit: number) => {
        setPage(1); // Reset to first page
        setLimitLocal(newLimit);
    }, []);

    const setSort = useCallback((id: string, direction: 'asc' | 'desc') => {
        console.log('sort change', id, direction);
        setSortedColumnId(id);
        setSortDirection(direction);
        setPage(1);
        
        // Save sort preferences to localStorage
        setLocalStorageItem('profitBySkuSortPreferences', {
            sortedColumnId: id,
            sortDirection: direction
        });
    }, []);

    const { data: overviewData, isLoading: isOverviewLoading, error: overviewError } = useCache(
        computeCacheKey(timeFrame, "/seller-analytics/profit-by-product/overview"), 
        shouldFetchOverview ? () => getSkuProfitabilityBreakdown(timeFrame) : null
    );
    const { data: profitabilityData, isLoading: isProfitabilityLoading, error: profitabilityError } = useCache<ProfitabilityByProductResponseBody>(
        computeCacheKey(timeFrame, "/seller-analytics/profit-by-product/breakdown"), 
        shouldFetchProfitability ? () => getSkuProfitability({ interval: timeFrame }) : null
    );
    
    // Stop retrying when errors occur and show error modal
    useEffect(() => {
        if (overviewError && shouldFetchOverview) {
            console.error('Overview data fetch failed:', overviewError);
            handleErrorResponseGenerically([{
                message: 'Unable to retrieve analytics data. Please try again later.',
            }]);
            setShouldFetchOverview(false);
        }
        
        if (profitabilityError && shouldFetchProfitability) {
            console.error('Profitability data fetch failed:', profitabilityError);
            handleErrorResponseGenerically([{
                message: 'Unable to retrieve analytics data. Please try again later.',
            }]);
            setShouldFetchProfitability(false);
        }
    }, [overviewError, profitabilityError, shouldFetchOverview, shouldFetchProfitability, handleErrorResponseGenerically]);
    
    // Reset retry flags when timeFrame changes
    useEffect(() => {
        setShouldFetchOverview(true);
        setShouldFetchProfitability(true);
    }, [timeFrame]);

    const filteredAndPaginatedData = useMemo<PaginatedData<ProductProfitDetails>>(() => {
        if (!profitabilityData) return null;
    
        // Filter data based on search term
        let filtered = profitabilityData.products.filter(item => {
          const searchLower = search.toLowerCase();
          return (
              item.sku.toLowerCase().includes(searchLower) ||
              item.name.toLowerCase().includes(searchLower)
          );
        });
    
        if (sortedColumnId && sortDirection) {
          filtered = [...filtered].sort((a, b) => {
            const aValue = a[sortedColumnId];
            const bValue = b[sortedColumnId];
    
            if (typeof aValue === 'number' && typeof bValue === 'number') {
              return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
            }
    
            if (typeof aValue === 'string' && typeof bValue === 'string') {
              return sortDirection === 'asc'
                  ? aValue.localeCompare(bValue)
                  : bValue.localeCompare(aValue);
            }
    
            return 0;
          });
        }
    
        // Calculate total pages
        const totalItems = filtered.length;
        const totalPages = Math.ceil(totalItems / limit);
    
        // Get paginated subset
        const startIndex = (page - 1) * limit;
        const endIndex = startIndex + limit;
        const paginatedItems = filtered.slice(startIndex, endIndex);
    
        return {
          items: paginatedItems,
          total: totalItems,
          page,
          limit,
          totalPages
        };
      }, [profitabilityData, search, page, limit, sortedColumnId, sortDirection]);

    const [_, navigate] = useLocation();

    const tableContainerRef = useRef<HTMLDivElement>(null);

    // useEffect(() => {
    //     fetchOverviewData();
    // }, [timeFrame]);

    // useEffect(() => {
    //     fetchProfitabilityData()
    // }, [timeFrame])

    const columns: TableColumn<ProductProfitDetails>[] = [
        {
            title:  'Image',
            id: 'image',
            dataKey: 'image',
            sortable: false,
            pinned: 'left',
            width: '4rem',
            renderRowCell: (row) => {
                return (
                    <button className={`${styles.button} ${styles.imageButton}`} onClick={() => { navigate(`/profit-by-sku/${encodeURIComponent(row.getCellData('sku'))}?analytics=true`)}}>
                        <img src={row.getCellData('image')} alt={row.getCellData('name')} className={styles.image}/>
                    </button>
                )
            },
        },
        {
            title: 'Product',
            id: 'name',
            dataKey: 'name',
            sortable: true,
            width: '19rem',
            // pinned: 'left',
            renderRowCell: (row) => {
                return (
                    <button className={styles.button} onClick={() => { navigate(`/profit-by-sku/${encodeURIComponent(row.getCellData('sku'))}?analytics=true`)}}>
                        <span>
                            {row.getCellData('name')}
                        </span>
                    </button>
                )
            },
            renderSkeleton: () => {
                return (
                    <div className={styles.rowCell}>
                        <Skeleton width={'auto'}/>
                    </div>
                )
            }
        },
        {
            title: 'SKU',
            id: 'sku',
            dataKey: 'sku',
            sortable: true,
            width: '7.5rem',
            renderRowCell: (row) => {
                return (
                    <button className={`${styles.button} ${styles.rightAlign}`} onClick={() => { navigate(`/profit-by-sku/${encodeURIComponent(row.getCellData('sku'))}?analytics=true`)}}>
                        <span>
                            {row.getCellData('sku')}
                        </span>
                    </button>
                )
            },
            renderHeaderCell: () => {
                return (
                    <span className={styles.headerCell}>
                        SKU
                    </span>
                )
            }
        },
        {
            title: 'Revenue',
            id: 'revenue',
            dataKey: 'revenue',
            width: '7.5rem',
            sortable: true,
            renderRowCell: (row) => {
                return (
                    <button className={`${styles.button} ${styles.rightAlign}`} onClick={() => {
                        navigate(`/profit-by-sku/${encodeURIComponent(row.getCellData('sku'))}?analytics=true`)
                    }}>
                        <span>
                            {formatValue(row.getCellData('revenue'), 'currency')}
                        </span>
                    </button>
                )
            }
        },
        {
            title: 'Profit',
            id: 'profit',
            dataKey: 'profit',
            width: '7.5rem',
            sortable: true,
            renderRowCell: (row) => {
                return (
                    <button className={`${styles.button} ${styles.rightAlign}`} onClick={() => {
                        navigate(`/profit-by-sku/${encodeURIComponent(row.getCellData('sku'))}?analytics=true`)
                    }}>
                        <span>
                            {formatValue(row.getCellData('profit'), 'currency')}
                        </span>
                    </button>
                )
            }
        },
        {
            title: 'Units Sold',
            id: 'unitsSold',
            dataKey: 'unitsSold',
            width: '7.5rem',
            sortable: true,
            renderRowCell: (row) => {
                return (
                    <button className={`${styles.button} ${styles.rightAlign}`} onClick={() => {
                        navigate(`/profit-by-sku/${encodeURIComponent(row.getCellData('sku'))}?analytics=true`)
                    }}>
                        <span>
                            {row.getCellData('unitsSold')}
                        </span>
                    </button>
                )
            }
        },
        {
            title: 'Buy Cost',
            id: 'avgBuyCost',
            dataKey: 'avgBuyCost',
            sortable: true,
            width: '7.5rem',
            renderRowCell: (row) => {
                return (
                    <button className={`${styles.button} ${styles.rightAlign}`} onClick={() => {
                        navigate(`/profit-by-sku/${encodeURIComponent(row.getCellData('sku'))}?analytics=true`)
                    }}>
                        <span>
                            {formatValue(row.getCellData('avgBuyCost'), "currency")}
                        </span>
                    </button>
            )
            }
        },
        {
            title: 'List Price',
            id: 'listPrice',
            dataKey: 'listPrice',
            width: '7.5rem',
            sortable: true,
            renderRowCell: (row) => {
                return (
                    <button className={`${styles.button} ${styles.rightAlign}`} onClick={() => {
                        navigate(`/profit-by-sku/${encodeURIComponent(row.getCellData('sku'))}?analytics=true`)
                    }}>
                        <span>
                            {formatValue(row.getCellData('listPrice'), 'currency')}
                        </span>
                    </button>
                )
            }
        },
        {
            title: 'In Stock Qty',
            id: 'inStockQty',
            dataKey: 'inStockQty',
            width: '7.5rem',
            sortable: true,
            renderRowCell: (row) => {
                return (
                    <button className={`${styles.button} ${styles.rightAlign}`} onClick={() => {
                        navigate(`/profit-by-sku/${encodeURIComponent(row.getCellData('sku'))}?analytics=true`)
                    }}>
                        <span>
                           {row.getCellData('inStockQty')}
                        </span>
                    </button>
                )
            }
        },
        {
            title: 'Refunds',
            id: 'refunds',
            dataKey: 'refunds',
            width: '7.5rem',
            sortable: true,
            renderRowCell: (row) => {
                return (
                    <button className={`${styles.button} ${styles.rightAlign}`} onClick={() => {
                        navigate(`/profit-by-sku/${encodeURIComponent(row.getCellData('sku'))}?analytics=true`)
                    }}>
                        <span>
                            {row.getCellData('refunds')}
                        </span>
                    </button>
                )
            }
        }
    ]

    // console.log("profitabilityData", profitabilityData);

    const cardAnalytics = useMemo(() => {
        return ([
            {
                sku: overviewData?.highestProfit?.sku,
                label: "Highest Profit",
                value: formatValue((overviewData?.highestProfit?.total ? overviewData.highestProfit.total : 0), "currency"),
                name: overviewData?.highestProfit?.productName,
                image: overviewData?.highestProfit?.image,
            },
            {
                sku: overviewData?.highestRevenue?.sku,
                label: "Highest Revenue",
                value: formatValue((overviewData?.highestRevenue?.total ? overviewData.highestRevenue.total : 0), "currency"),
                name: overviewData?.highestRevenue?.productName,
                image: overviewData?.highestRevenue?.image,
            },
            {
                sku: overviewData?.highestUnitsSold?.sku,
                label: "Highest Units Sold",
                value: formatValue((overviewData?.highestUnitsSold?.total ? overviewData.highestUnitsSold.total : 0), "number"),
                name: overviewData?.highestUnitsSold?.productName,
                image: overviewData?.highestUnitsSold?.image,
            },
            {
                sku: overviewData?.highestRefunds?.sku,
                label: "Most Refunds",
                value: formatValue((overviewData?.highestRefunds?.total ? overviewData.highestRefunds.total : 0), "number"),
                name: overviewData?.highestRefunds?.productName,
                image: overviewData?.highestRefunds?.image,
            },
            {
                label: "Amount of SKUs",
                value: formatValue((overviewData?.totalSKUs ? overviewData.totalSKUs : 0), "number"),
                subHeading: "Total",
            }
        ])
    }, [overviewData]);

    return (
        <PageContainer>
            <div className={styles.container}>
                <div className={styles.header}>
                    <h1 className={styles.title}>SKU Profitability</h1>
                    <DateRangeButtons
                        timeFrame={timeFrame}
                        setTimeFrame={setTimeFrame}
                    />
                </div>

                <div className={styles.cards}>
                    {/* Content for SKU profitability data will go here */}
                    {cardAnalytics.map((card, index) => {
                        const href = card.sku ? `/profit-by-sku/${encodeURIComponent(card.sku)}/?analytics=true` : undefined;
                        return (
                            <div key={index}>
                                {card.image ? <Card
                                    key={index}
                                    label={card.label}
                                    productName={card.name}
                                    value={card.value}
                                    isLoading={isOverviewLoading}
                                    href={href}
                                    imageUrl={card.image}
                                /> :
                                    <TotalCard
                                        key={index}
                                        label={card.label}
                                        value={card.value}
                                        isLoading={isOverviewLoading}
                                        subHeading={card.subHeading}
                                    />
                                }
                            </div>
                        );
                    })}
                </div>
                <div className={styles.tableContainer}>
                    <Table
                        tableName={'profit-by-sku-table-2'}
                        showSort
                        data={filteredAndPaginatedData?.items || []}
                        tableContainerRef={tableContainerRef}
                        columns={columns}
                        isLoading={isProfitabilityLoading}
                        skeletonRows={limit}
                        sortDirection={sortDirection}
                        sortedColumnId={sortedColumnId}
                        header={<TextInput
                            className={styles.searchInput}
                            placeholder={"Product Name, SKU, or ASIN"}
                            prefix={<IconSearch/>}
                            value={search}
                            inputAutofocus
                            onChange={(value) => setSearch(value)}
                        />}
                        footer={<Pagination
                            total={filteredAndPaginatedData?.total || 0}
                            pageSize={filteredAndPaginatedData?.limit || 10}
                            onChangePageSize={(value) => setLimit(value)}
                            page={filteredAndPaginatedData?.page || 1}
                            onChangePage={(newPage) => setPage(newPage)}
                            showPageSelector={false}
                            pageSizeOptions={[5, 10, 20]}
                        />}
                        onSort={setSort}
                    />
                </div>
            </div>
        </PageContainer>
    );
}

export default ProfitBySkuPage;